import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    Output,
    QueryList,
    TrackByFunction,
    ViewChild,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { fromEvent, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
    first,
    map,
    shareReplay,
    takeUntil,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { ContenteditableTriggerDirective } from '../../directives/contenteditable-trigger.directive';
import { fadeInOut } from '../../animations/fade-in-out.animation';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { isArray } from '@datorama/akita';
import {
    IndicatorState,
    SbPendingButtonIndicatorComponent
} from '../sb-pending-button-indicator/sb-pending-button-indicator.component';

export interface Badge<D = any> {
    title: string;
    variant?: 'info' | 'success' | 'warning' | 'critical' | 'primary' | 'secondary';
    outline?: boolean;
    dismissible?: boolean;
    size?: 'xs' | 'sm' | 'md';
    data?: D;
    pending?: false | 'add' | 'delete';
    color?: string;
}

export interface BadgesChangeEvent<D = any> {
    type: 'delete' | 'add' | 'toggle-active' | 'set' | 'rearrange' | 'not-allowed';
    badge?: Badge<D>;
    badges: Array<Badge<D>>;
    final?: boolean;
    message?: string;
}

@Component({
    selector: 'sb-badges',
    standalone: true,
    imports: [
        CommonModule,
        TablerIconsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        ContenteditableTriggerDirective,
        MatTooltipModule,
        SbPendingButtonIndicatorComponent
    ],
    templateUrl: './sb-badges.component.html',
    styleUrls: ['./sb-badges.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeInOut]
})
export class SbBadgesComponent implements OnDestroy {
    @Input() showTooltip = true;

    private _direction: 'row' | 'column' | 'row-reverse' | 'column-reverse' = 'row';

    @Input() set direction(val: 'column' | 'row' | 'row-reverse' | 'column-reverse') {
        if (val) {
            this._direction = val;
            this._reverse = val.includes('reverse');
        }
    }

    @Input() set alignItems(val: 'start' | 'end' | 'center') {
        this._alignItems = val;
    }
    private _alignItems: 'start' | 'end' | 'center' = 'start';
    get alignItems() {
        return this._alignItems;
    }

    get direction() {
        return this._direction;
    }
    private _reverse = false;
    set reverse(val: boolean) {
        this._reverse = val;
    }

    get reverse() {
        return this._reverse;
    }

    get useBadgeMaxCount(): boolean {
        return this._useBadgeMaxCount;
    }

    @Input() set useBadgeMaxCount(value: boolean) {
        this._useBadgeMaxCount = value;
    }
    get maxBadgeCount(): number {
        return this._maxBadgeCount;
    }

    @Input() set maxBadgeCount(value: number) {
        this._maxBadgeCount = value;
    }
    @ViewChild('badges', { read: ViewContainerRef }) badgesRef: ViewContainerRef;
    @ViewChildren('badges') badgeElements: QueryList<ElementRef>;

    get wrap(): boolean {
        return this._wrap;
    }

    @Input() set wrap(value: boolean) {
        this._wrap = value;
    }

    get pending(): Badge['pending'] {
        return this._pending;
    }

    set pending(value: Badge['pending']) {
        this._pending = value;
        this._cdr.markForCheck();
    }
    private _unsubscribe$ = new Subject();
    @ViewChild('newBadge')
    set newBadge(content: ElementRef<HTMLSpanElement>) {
        if (content) {
            this._newBadge = content;
            this._newBadge.nativeElement.focus();
        }
    }
    get showAddButton(): boolean {
        return this._showAddButton;
    }

    @Input() set showAddButton(value: boolean) {
        this._showAddButton = value;
    }

    get outline(): Badge['outline'] {
        return this._outline;
    }

    @Input() set outline(value: Badge['outline']) {
        this._outline = value + '' === 'true';
    }

    get size(): Badge['size'] {
        return this._size;
    }

    @Input() set size(value: Badge['size']) {
        this._size = value;
    }

    get variant(): Badge['variant'] {
        return this._variant;
    }

    @Input() set variant(value: Badge['variant']) {
        this._variant = value;
    }
    get dismissible(): boolean {
        return this._dismissible;
    }

    @Input() set dismissible(value: boolean) {
        this._dismissible = value;
    }
    get allowActive(): boolean | 'multiple' | 'multiple-not-empty' {
        return this._allowActive;
    }

    @Input() set allowActive(value: boolean | 'multiple' | 'multiple-not-empty') {
        this._allowActive = value;
    }
    get badges(): Array<Badge> {
        return this._badges;
    }

    @Input() set badges(badges: Array<Badge>) {
        badges = isArray(badges) ? badges : [];
        const old = badges;
        badges = badges.reduce((acc, current) => {
            acc.push({
                ...current,
                variant: current.variant || this.variant,
                outline: this.outline === false ? false : current.outline ?? true,
                size: current.size || this.size
            });
            return acc;
        }, []);

        this._badges = badges;
        if (
            // if the badges have changed (data value is excluded)...
            JSON.stringify(
                this.badges.map((badge) => {
                    // don't stringify data property as it may contain functions
                    const { data, ...c } = badge;
                    return c;
                })
            ) !==
            JSON.stringify(
                old.map((badge) => {
                    // don't stringify data property as it may contain functions
                    const { data, ...c } = badge;
                    return c;
                })
            )
        ) {
            this.badgesChange.emit({ type: 'set', badges: this.badges });
        }
    }
    @Output() badgesChange = new EventEmitter<BadgesChangeEvent>();
    @Output() activeBadgeChange = new EventEmitter<Badge>();
    private _allowActive: boolean | 'multiple' | 'multiple-not-empty' = false;
    private _dismissible = false;
    activeBadge: Badge;
    private _newBadge: ElementRef<HTMLSpanElement>;

    private _badges: Array<Badge> = [];
    @HostBinding('class.flex-wrap') private _wrap = false;
    private _variant: Badge['variant'] = 'info';
    private _size: Badge['size'] = 'md';
    private _outline: Badge['outline'] = true;
    private _showAddButton = false;
    private _pending: Badge['pending'] = false;
    private _maxBadgeCount = 7;
    private _useBadgeMaxCount = true;
    public window: Window;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private _cdr: ChangeDetectorRef
    ) {
        this.window = this.document.defaultView;
    }

    delete(index: number): void {
        this.addIndicatorState = 'loading';
        const deletedBadge = this._badges[index];
        //this._badges.splice(index, 1);
        this.badgesChange.emit({
            type: 'delete',
            badge: deletedBadge,
            badges: [...[...this.badges].splice(index, 1)]
        });
        deletedBadge.pending = 'delete';
        this.pending = 'delete';

        this.addIndicatorState$
            .pipe(
                takeWhile((state) => state !== null, true),
                takeUntil(this._unsubscribe$),
                tap((state) => {
                    if (state === null) {
                        this._badges.splice(index, 1);
                        deletedBadge.pending = false;
                        this.pending = false;
                    } else if (state === 'error') {
                        deletedBadge.pending = false;
                        this.pending = false;
                    }
                })
            )
            .subscribe((state) => {});
    }

    toggleActiveState(index: number): void {
        let emitChange = true;
        // if there is an active badge...
        if (
            this.activeBadge &&
            this.activeBadge !== this._badges[index] &&
            this._allowActive !== 'multiple' &&
            this._allowActive !== 'multiple-not-empty'
        ) {
            // ...set it to outline (style of inactive badge)
            this.activeBadge.outline = true;
        }
        // declare new active badge
        this.activeBadge = this._badges[index];

        // toggle outline property of active badge
        if (this.allowActive === 'multiple') {
            this.activeBadge.outline = !this.activeBadge.outline;
        } else if (this.allowActive === 'multiple-not-empty') {
            if (
                this.activeBadge.outline === true ||
                (this.activeBadge.outline === false &&
                    this._badges.filter((badge) => badge.outline === false).length > 1)
            ) {
                this.activeBadge.outline = !this.activeBadge.outline;
            } else {
                emitChange = false;
            }
        } else {
            this.activeBadge.outline = false;
        }
        if (emitChange) {
            this.activeBadgeChange.emit(this.activeBadge);
            this.badgesChange.emit({
                type: 'toggle-active',
                badge: this.activeBadge,
                badges: this.badges
            });
        } else if (this.allowActive === 'multiple-not-empty') {
            this.badgesChange.emit({
                type: 'not-allowed',
                badge: this.activeBadge,
                badges: this.badges,
                message: 'You have to have at least one selected.'
            });
        }
    }

    showEnterNewBadge = false;

    addNewBadge() {
        this.addIndicatorState = null;
        this.showEnterNewBadge = !this.showEnterNewBadge;
        //this.contenteditableTrigger.focus();
    }
    addBadge(title: string, badgeEl: HTMLSpanElement) {
        this.addIndicatorState = 'loading';
        this._badges = [
            ...this._badges,
            {
                title,
                pending: 'add',
                size: this.size,
                variant: this.variant,
                outline: this.outline
            }
        ];
        this.badgesChange.emit({
            type: 'add',
            badge: { title },
            badges: this.badges
        });
        this.pending = 'add';
        this.newBadgeCtrl.setValue('');
        badgeEl.innerText = '';
        this.addIndicatorState$
            .pipe(
                takeWhile((state) => state !== 'success' && state !== 'error', true),
                takeUntil(this._unsubscribe$),
                tap((state) => {
                    if (state === 'success') {
                        this.showEnterNewBadge = false;
                    }
                    this.pending = false;
                })
            )
            .subscribe((state) => {});
    }

    @Input() set suggestions(suggestions: Array<string>) {
        this._suggestions$.next(suggestions);
    }

    _suggestions$ = new ReplaySubject<Array<string>>(1);

    newBadgeCtrl = new FormControl<string>('');
    suggestions$ = this.newBadgeCtrl.valueChanges.pipe(
        withLatestFrom(this._suggestions$),
        map(([value, suggestions]) => {
            if (!value || value.length === 0) {
                return [];
            }
            return suggestions
                ? suggestions
                      .filter(
                          (suggestion) =>
                              suggestion.toLowerCase().includes(value.toLowerCase()) &&
                              !this._badges.find((badge) => badge.title === suggestion)
                      )
                      .slice(0, 5)
                : [];
        })
    );

    @Input() set addIndicatorState(addIndicatorState: IndicatorState) {
        this._addIndicatorState$.next(addIndicatorState);
    }

    _indicatorResetTimeout: NodeJS.Timeout;
    get addIndicatorState$() {
        return this._addIndicatorState$.asObservable().pipe(
            tap((state) => {
                /*  The dismiss button (for delete) is not shown when indicator state is set to success
                    or error (to prevent it from being deleted directly when added).
                    To enable it again, we need to reset the indicator state after a delay.
                    This allows the user to delete a badge after it has been added and the animation for adding it has finished.
                */
                if (state === 'success' || state === 'error') {
                    // clear timeout if it exists
                    if (this._indicatorResetTimeout) {
                        clearTimeout(this._indicatorResetTimeout);
                    }

                    // reset the indicator state after 3 seconds (enough to allow the animation to finish)
                    this._indicatorResetTimeout = setTimeout(() => {
                        this.addIndicatorState = null;
                        this.pending = false;
                    }, 3000);
                }
            }),
            shareReplay(1)
        );
    }

    _addIndicatorState$: ReplaySubject<IndicatorState> = new ReplaySubject(0);
    trackByFn: TrackByFunction<Badge> = (index, item) => item.title?.toLowerCase();

    // Drag definitions, logic and animation controlling
    @Input() allowDragArrange = false;
    dragSubscription$: Subscription;
    dropSubscription$: Subscription;
    dragHoverIndex: number;
    prevDragIndex: number;
    draggedIndex: number = null;

    private _setDraggedBadge(index: number) {
        this.draggedIndex = index;
    }

    private _resetDraggedBadge() {
        this.draggedIndex = null;
    }

    /**
     * Called on drag start
     * Initialize drag logic, including determining which badge is being dragged and setting the drag image
     * @param event - the drag start event
     * @param index - the index of the badge that is being dragged
     */
    onDragStart(event: DragEvent, index: number): void {
        event.dataTransfer.effectAllowed = 'move';

        this._setDraggedBadge(index);
        this.dropSubscription$ = fromEvent(
            this.badgeElements.get(index).nativeElement,
            'dragend'
        )
            .pipe(
                first(),
                tap((event) => this.onDragEnd(event)),
                takeUntil(this._unsubscribe$) // Safeguard, but should be unsubscribed in dragend event
            )
            .subscribe();
    }

    /**
     * Called while dragging
     * @param event - the dragover event
     * @param index - the index of the badge currently being dragged over
     * @returns
     */
    onDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        // If we did not switch index, do nothing (performance)
        if (index === this.dragHoverIndex || index === this.prevDragIndex) {
            return;
        }
        this.prevDragIndex = this.dragHoverIndex;
        this.dragHoverIndex = index;
        const badgesCopy = this.badges.slice();
        const movedItem = badgesCopy.splice(this.draggedIndex, 1)[0];
        const s1 = badgesCopy.slice(0, index);
        const s2 = badgesCopy.slice(index);

        this.badges = [].concat(s1, movedItem, s2);
        this.draggedIndex = index; // reset drag index to new index of dragged item!

        this.badgesChange.emit({
            type: 'rearrange',
            badges: this.badges
        });
    }

    /**
     * Called when the dragging is ended
     * @param event - the dragend event
     */
    onDragEnd(event) {
        // Note: We use dragend rather than drop event to as drop will not fire if released
        // while out-of-bounds for a legal drag
        event.preventDefault();

        // Emit drop event to indicate we are done with rearranging the badges
        this.badgesChange.emit({
            type: 'rearrange',
            badges: this.badges,
            final: true
        });
        this._resetDraggedBadge();
        this.dragHoverIndex = null;
        this.dropSubscription$.unsubscribe();
    }

    ngOnDestroy(): void {
        this._unsubscribe$.next(true);
        this._unsubscribe$.complete();
    }

    showAll() {
        this.useBadgeMaxCount = false;
    }
}
